import { useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios, { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { getLoginPartialPath, getLogoutUrl } from '@common/libs/auth';
import { useLocalStorage } from 'usehooks-ts';

const RETRY_COUNT = 3;
const LOGIN_ATTEMPTS = 'loginAttempts';

export function useAxiosInterceptor() {
  const navigate = useNavigate();
  const loginPath = getLoginPartialPath();
  const [loginAttempts, setLoginAttempts] = useLocalStorage(LOGIN_ATTEMPTS, 0);

  const resetLoginAttempts = useCallback(() => setLoginAttempts(0), [setLoginAttempts]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => {
        if (loginAttempts > 0) resetLoginAttempts();
        return response;
      },
      (error: AxiosError) => {
        let message = error.message;
        if (axios.isAxiosError<{ message: string } | string>(error) && error.response) {
          if (error.response.data && error.response.data !== '') {
            if (typeof error.response.data === 'string') {
              message = error.response.data;
            } else if (error.response.data.message) {
              message = error.response.data.message;
            } else {
              message = JSON.stringify(error.response.data);
            }
          }
          if (error.response.status === 401) {
            setLoginAttempts((prev) => prev + 1);
            if (loginAttempts >= RETRY_COUNT) {
              navigate(getLogoutUrl());
              resetLoginAttempts();
            } else {
              navigate(loginPath);
            }
          }
        }
        if (!error?.config?.headers['Disable-Notification']) toast.error(message);
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate, loginAttempts, loginPath, resetLoginAttempts, setLoginAttempts]);
}
